import { Link } from "react-router";
import type { LaunchPlan } from "../../api/paidReady";
import { Button } from "../../components/Button";
import { BackButton } from "../../components/BackButton";
import { Guide } from "../../components/Guide";
import { ScreenHeader } from "../../components/ScreenHeader";
import { money, shortDate } from "../../lib/format";

const KYC_LABEL: Record<string, string> = {
  verified: "Verified",
  pending: "Submitted, awaiting approval",
  notstarted: "Not started",
};

/** The one-page exit artifact at Step 4. Printable. */
export function LaunchPlanScreen({ plan, justDone }: { plan: LaunchPlan; justDone: boolean }) {
  const main = plan.profiles[0];
  const h4 = "mb-1 mt-4 text-[13px] font-bold uppercase tracking-[0.06em] text-text-muted";
  return (
    <div>
      <ScreenHeader
        kicker={`Step 4 · ${justDone ? "You did it" : "Your Launch Plan"}`}
        title={justDone ? "You're Work-Enabled. Here's your Launch Plan." : "Your Launch Plan"}
        sub="One page with everything you need to land your first paid gig. Print it or save it as a PDF."
      />
      <Guide guideKey="launch" done={!justDone} />
      <div className="doc rounded-[var(--radius-card)] border border-border bg-surface p-5 text-[14px]">
        <h2 className="text-[22px] text-accent">Launch Plan</h2>
        <div className="text-[12.5px] text-text-muted">
          {plan.name} · {plan.trackLabel} · Work-Enabled {shortDate(plan.workEnabledAt)}
        </div>
        <h4 className={h4}>What I do</h4>
        <div>{plan.niche}</div>
        <h4 className={h4}>Where clients find me</h4>
        <ul className="m-0 pl-5">
          {plan.profiles.map((p) => (
            <li key={p.id}>
              {p.platform.name}: {p.profileUrl} (KYC:{" "}
              {KYC_LABEL[p.kycStatus ?? ""] ?? "Not recorded"})
            </li>
          ))}
        </ul>
        <h4 className={h4}>My proof</h4>
        <div>
          Portfolio: {plan.portfolioUrl}
          {plan.qualityPct != null ? ` · Peer quality score ${plan.qualityPct}%` : ""}
        </div>
        <h4 className={h4}>My rates</h4>
        <table className="w-full text-[14px]">
          <tbody>
            {plan.packages.map((pkg) => (
              <tr key={pkg.name ?? ""} className="border-b border-border">
                <td className="py-1.5">
                  <b>{pkg.name}</b>
                  <div className="text-[12.5px] text-text-muted">{pkg.incl}</div>
                </td>
                <td className="py-1.5">{pkg.days} days</td>
                <td className="py-1.5 text-right tabular">{money(pkg.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <h4 className={h4}>How I get paid</h4>
        <div>{plan.payMethods.join(", ")}</div>
        <h4 className={h4}>First gigs to target</h4>
        <ul className="m-0 pl-5">
          {plan.targetJobs.length ? (
            plan.targetJobs.map((job) => (
              <li key={job.code}>
                Gigs like "{job.title}" (${job.lo} to ${job.hi})
              </li>
            ))
          ) : (
            <li>Search your platform for jobs matching your niche</li>
          )}
        </ul>
        {plan.bestBid ? (
          <>
            <h4 className={h4}>My best practice bid</h4>
            <div>
              {plan.bestBid.pct}% on "{plan.bestBid.job.title}". Reuse its structure: name, need,
              proof, plan, price, question.
            </div>
          </>
        ) : null}
        <h4 className={h4}>My next 7 days</h4>
        <ul className="m-0 pl-5">
          <li>
            Send 5 real proposals on {main ? main.platform.name : "your platform"}, each tailored to
            the client, using your best-bid structure.
          </li>
          <li>Finish KYC on any platform where it isn't verified yet.</li>
          <li>Share your portfolio link with 3 people who could hire or refer you.</li>
          <li>
            Send your client agreement before starting any job, and invoice as soon as you deliver.
          </li>
          <li>Come back for Step 5 once your first gig is in.</li>
        </ul>
      </div>
      <div className="mt-4 flex flex-wrap gap-2.5 print:hidden">
        <Link
          to="/gig/5"
          className="inline-flex min-h-11 items-center justify-center rounded-[var(--radius-control)] bg-accent px-[22px] text-[14.5px] font-bold text-on-accent hover:bg-accent-pressed"
        >
          Continue to Step 5 →
        </Link>
        <Button variant="ghost" onClick={() => window.print()}>
          Print or save as PDF
        </Button>
        <BackButton to="/hub" label="Back to my steps" />
      </div>
    </div>
  );
}
